import type { JSONContent } from '@tiptap/react';

const STORAGE_PREFIX = 'rudi:rich-text-draft:v1';
const AUTH_STORAGE_KEY = 'rudi_auth_tokens';
export const RICH_TEXT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1_000;

export interface RichTextDraftScope {
  tenantId: string;
  userId: string;
  recordUuid: string;
  fieldKey: string;
}

export interface RichTextDraftContent {
  markdown: string;
  tiptapJson: JSONContent;
}

export interface RichTextDraftInput {
  content: RichTextDraftContent;
  localRevision: number;
  updatedAt: number;
}

export interface RichTextDraft extends RichTextDraftInput {
  scope: RichTextDraftScope;
}

function encodeKeyPart(value: string): string {
  return encodeURIComponent(value);
}

function storageKey(scope: RichTextDraftScope): string {
  return [
    STORAGE_PREFIX,
    encodeKeyPart(scope.tenantId),
    encodeKeyPart(scope.userId),
    encodeKeyPart(scope.recordUuid),
    encodeKeyPart(scope.fieldKey),
  ].join(':');
}

function userStoragePrefix(tenantId: string, userId: string): string {
  return `${STORAGE_PREFIX}:${encodeKeyPart(tenantId)}:${encodeKeyPart(userId)}:`;
}

function browserStorage(): Storage | null {
  return typeof window === 'undefined' ? null : window.localStorage;
}

export function getCurrentRichTextDraftScope(
  recordUuid: string,
  fieldKey: string
): RichTextDraftScope | null {
  const storage = browserStorage();
  if (!storage) return null;
  try {
    const stored = storage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    const session = recordValue(JSON.parse(stored));
    const user = recordValue(session?.user);
    if (typeof user?.id !== 'string') return null;
    const tenantId =
      typeof user.tenantId === 'string'
        ? user.tenantId
        : typeof user.tenantSlug === 'string'
          ? user.tenantSlug
          : 'personal';
    return { tenantId, userId: user.id, recordUuid, fieldKey };
  } catch {
    return null;
  }
}

export function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

function isDraft(value: unknown): value is RichTextDraft {
  if (typeof value !== 'object' || value === null) return false;
  const draft = value as Partial<RichTextDraft>;
  return (
    typeof draft.scope?.tenantId === 'string' &&
    typeof draft.scope.userId === 'string' &&
    typeof draft.scope.recordUuid === 'string' &&
    typeof draft.scope.fieldKey === 'string' &&
    typeof draft.content?.markdown === 'string' &&
    typeof draft.content.tiptapJson === 'object' &&
    draft.content.tiptapJson !== null &&
    typeof draft.localRevision === 'number' &&
    Number.isSafeInteger(draft.localRevision) &&
    draft.localRevision >= 0 &&
    typeof draft.updatedAt === 'number' &&
    Number.isFinite(draft.updatedAt)
  );
}

export function putRichTextDraft(scope: RichTextDraftScope, input: RichTextDraftInput): void {
  const storage = browserStorage();
  if (!storage) return;
  const draft: RichTextDraft = { scope, ...input };
  try {
    storage.setItem(storageKey(scope), JSON.stringify(draft));
  } catch {
    // A full or disabled browser store must not stop the editor from saving remotely.
  }
}

export function getRichTextDraft(
  scope: RichTextDraftScope,
  now: number = Date.now()
): RichTextDraft | null {
  const storage = browserStorage();
  if (!storage) return null;
  const key = storageKey(scope);
  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    const draft: unknown = JSON.parse(serialized);
    if (!isDraft(draft) || now - draft.updatedAt >= RICH_TEXT_DRAFT_TTL_MS) {
      storage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function clearRichTextDraft(scope: RichTextDraftScope): void {
  browserStorage()?.removeItem(storageKey(scope));
}

function storedKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key !== null) keys.push(key);
  }
  return keys;
}

export function clearRichTextDraftsForUser(tenantId: string, userId: string): void {
  const storage = browserStorage();
  if (!storage) return;
  const prefix = userStoragePrefix(tenantId, userId);
  storedKeys(storage)
    .filter(key => key.startsWith(prefix))
    .forEach(key => storage.removeItem(key));
}

export function clearAllRichTextDrafts(): void {
  const storage = browserStorage();
  if (!storage) return;
  storedKeys(storage)
    .filter(key => key.startsWith(STORAGE_PREFIX))
    .forEach(key => storage.removeItem(key));
}
